import { Box, Button, Stack, Typography } from "@mui/material";
import PendingMeetingDisplay from "../components/meetings/PendingMeetingDisplay";
import { useEffect, useState } from "react";
import API from "../api/axiosConfig";
import { useToast } from "../context/ToastContext";
import PendingReimbursementList from "../components/reimbursements/PendingReimbursementList";
import CandidateReferralList from "../components/candidates/CandidateReferralList.js";
import EditableField from "../components/EditableField.js";
import ResumeUpload from "../components/ResumeUpload.js";
import { useUser } from "../context/UserContext";

export default function ProfilePage() {
  const showToast = useToast();

  // -- STATE --
  // Initialize the user object
  const { user, setUser } = useUser();
  // Track the field being actively edited: null (none), birthday, experience, resume, etc...
  const [isEditing, setIsEditing] = useState(false);
  // File object being uploaded or downloaded
  const [resumeFile, setResumeFile] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  // does the user want to delete their resume?
  const [deleteResume, setDeleteResume] = useState(false);
  // this is the data in the edit fields
  const [editData, setEditData] = useState({});

  // Generate Experience years range 0-50
  const experienceOptions = Array.from({ length: 51 }, (_, i) => ({
    value: i.toString(),
    label: `${i} Years`,
  }));

  const contractOptions = [
    { value: "None", label: "None" },
    { value: "RIS1", label: "RIS1" },
  ];
  // this handles the editing changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    //Update the formData state
    setEditData((prevData) => ({
      ...prevData, // Copy all existing fields (so we don't lose them!)
      [name]: value, // Overwrite ONLY the field that changed
    }));
  };

  const handleFileChange = (file) => {
    setResumeFile(file);
    setEditData((prevData) => ({
      ...prevData,
      resumeFileName: file?.name || null,
    }));
    if (!file) {
      // if the file is null, user wants to delete resume
      setDeleteResume(true);
    } else {
      // if there is a file they don't want to delete
      setDeleteResume(false);
    }
  };

  useEffect(() => {
    if (user) {
      // When user gets loaded, fill editData
      setEditData(user);
    }
  }, [user]); // watches for user changes

  //Save Function
  const handleSave = async (field) => {
    const birthdate = new Date(editData.birthday);
    // Validate Birthday
    if (field === "birthday") {
      if (birthdate > new Date()) {
        alert("Error: You cannot have a future birthdate!");
        return;
      }
    }
    // split the time off of birthday to just be left with date
    const formattedDate = birthdate.toISOString().split("T")[0];
    // Create the body of the post request
    const formData = new FormData();
    if (editData.birthday) {
      formData.append("birthday", formattedDate);
    }
    if (editData.totalExperience) {
      formData.append("experience", editData.totalExperience);
    }
    if (editData.contractName) {
      formData.append("contractName", editData.contractName);
    }
    if (resumeFile && !deleteResume) {
      formData.append("resume", resumeFile);
    }
    formData.append("deleteResume", deleteResume);

    try {
      const response = await API.post("api/users/updateProfile", formData);
      showToast("Profile updated!", "success");
      setUser(response.data);
      setIsEditing(false);
      setDeleteResume(false);
    } catch (err) {
      if (err.response?.status !== 401) {
        console.error(`Error updating ${field}:`, err);
      }
      showToast("Error updating profile", "error");
    }
  };

  // Handle Preview
  const handlePreview = async () => {
    try {
      const response = await API.get(`/api/users/resume/download`, {
        responseType: "blob",
      });

      // Create a temp 'Object URL' that points to that data in memory
      const blobUrl = window.URL.createObjectURL(response.data);

      // Open temp URL in a new tab
      window.open(blobUrl, "_blank");

      // Clean up memory: Tell the browser it doesn't need to hold Blob
      // Wait a few seconds to ensure the new tab has finished loading
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 3000);
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error("Preview failed:", error);
        showToast("Error opening preview. Please try again.", "error");
      }
    }
  };

  // Handle Download
  const handleResumeDownload = async () => {
    if (isDownloading) return; // don't double download
    //Begin Process
    setIsDownloading(true);
    try {
      const response = await API.get(`/api/users/resume/download`, {
        responseType: "blob",
      });

      // Create a temp "invisible" URL for the file in memory
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;

      // Forces the filename and the Save action
      link.setAttribute("download", editData.resumeFileName || "resume.pdf");
      document.body.appendChild(link);
      link.click();

      //Clean up
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showToast("Could not download resume. Please try again.", "error"); // Not sure if we should tell people to try again
    } finally {
      setIsDownloading(false);
    }
  };

  // -- HELPERS --
  const formatDate = (isoDateString) => {
    if (!isoDateString) {
      return "Not set";
    }
    const date = new Date(isoDateString);
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  return (
    <Box
      sx={{
        maxWidth: "1200px",
        mx: "auto",
        px: 3,
        py: 4,
        width: "100%",
        boxSizing: "border-box",
        textAlign: "left",
      }}
    >
      {/* HEADER SECTION */}
      <Box
        sx={{
          mb: 4,
          pb: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          textAlign: "center",
        }}
      >
        <Typography variant="h4" fontWeight="700" sx={{ mb: 0.5 }}>
          {user?.name}
        </Typography>
        <Typography variant="subtitle1" sx={{ color: "text.secondary" }}>
          {user?.email}
        </Typography>
      </Box>

      {/* TWO-COLUMN GRID LAYOUT */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "340px minmax(0, 1fr)" },
          gap: 4,
          alignItems: "start",
          width: "100%",
        }}
      >
        {/* ─── LEFT COLUMN: PROFILE CARD & FORM FIELDS ─── */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            backgroundColor: "background.paper",
            p: 3,
            borderRadius: 2,
            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.05)",
            border: "1px solid",
            borderColor: "divider",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <Typography
            variant="h6"
            fontWeight="600"
            sx={{
              textAlign: "center",
              pb: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            Account Details
          </Typography>

          <EditableField
            label="Birthday"
            name="birthday"
            value={
              isEditing
                ? editData.birthday
                : formatDate(editData.birthday) || "None selected"
            }
            onChange={handleChange}
            isEditing={isEditing}
            type="date"
            variant="body1"
          />

          <EditableField
            label="Experience"
            name="totalExperience"
            value={String(editData.totalExperience) || "None selected"}
            onChange={handleChange}
            isEditing={isEditing}
            options={experienceOptions}
            variant="body1"
          />

          <EditableField
            label="Contract Name"
            name="contractName"
            value={editData.contractName || "None selected"}
            onChange={handleChange}
            isEditing={isEditing}
            options={contractOptions}
            variant="body1"
          />

          <Box
            sx={{
              mt: 1,
              pt: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <ResumeUpload
              isEditing={isEditing}
              isDownloading={isDownloading}
              deleteResume={deleteResume}
              resumeFile={resumeFile}
              fileName={editData.resumeFileName}
              variant="body1"
              onFileSelected={(file) => handleFileChange(file)}
              onFileDownload={handleResumeDownload}
              handlePreview={handlePreview}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            {!isEditing ? (
              <Button
                fullWidth
                variant="contained"
                onClick={() => setIsEditing(true)}
                sx={{ py: 1, fontWeight: "600", borderRadius: 1.5 }}
              >
                Edit Profile
              </Button>
            ) : (
              <Stack direction="row" gap={1.5} width="100%">
                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  disabled={JSON.stringify(editData) === JSON.stringify(user)}
                  onClick={() => handleSave()}
                  sx={{ py: 1, fontWeight: "600", borderRadius: 1.5 }}
                >
                  Save
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    setIsEditing(false);
                    setEditData(user);
                    setResumeFile(null);
                    setDeleteResume(false);
                  }}
                  sx={{ py: 1, fontWeight: "600", borderRadius: 1.5 }}
                >
                  Cancel
                </Button>
              </Stack>
            )}
          </Box>
        </Box>

        {/* ─── RIGHT COLUMN: INTERACTIVE STREAM LISTS ─── */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            minWidth: 0,
            width: "100%",
            flexShrink: 1,
            "& .MuiDataGrid-root, & .MuiTableContainer-root": {
              width: "100% !important",
              minWidth: "0 !important",
            },
          }}
        >
          {/* MEETINGS CARD */}
          <Box
            sx={{
              backgroundColor: "background.paper",
              p: 3,
              borderRadius: 2,
              boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.05)",
              border: "1px solid",
              borderColor: "divider",
              width: "100%",
              boxSizing: "border-box",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
            }}
          >
            <PendingMeetingDisplay />
          </Box>

          {/* REIMBURSEMENTS CARD */}
          <Box
            sx={{
              backgroundColor: "background.paper",
              p: 3,
              borderRadius: 2,
              boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.05)",
              border: "1px solid",
              borderColor: "divider",
              width: "100%",
              boxSizing: "border-box",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
            }}
          >
            <PendingReimbursementList />
          </Box>

          {/* REFERRALS CARD */}
          <Box
            sx={{
              backgroundColor: "background.paper",
              p: 3,
              borderRadius: 2,
              boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.05)",
              border: "1px solid",
              borderColor: "divider",
              width: "100%",
              boxSizing: "border-box",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
            }}
          >
            <CandidateReferralList />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
